import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useEffect } from 'react';
import { ScrollTop } from '@/components/element/ScrollTop';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { Link, useNavigate } from 'react-router-dom';
import { useWorkspaceUsers, useWorkspaceUsersInvalidate } from '@/hooks/api/0150_workspace_users';
import { useLoading } from '@/providers/LoadingProvider';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useCreatePurchaseOrderMember } from '@/hooks/api/0600_create_purchase_order_member';
import { usePurchaseOrderInvalidate } from '@/hooks/api/0570_purchase_order';
import { useTeamInvalidate } from '@/hooks/api/0210_team';
import { useDestroyPurchaseOrderMember } from '@/hooks/api/0610_destroy_purchase_order_member';
import { PurchaseOrderLogic } from '@/hooks/api/models/logics';
import { toastApiError } from '@/lib/toast';

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  user_id: number;
};

export const PurchaseOrderMembers = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle(`関連メンバー`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        {
          label: `プロジェクト（費用） - ${_t_!.name}`,
          path: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders`,
        },
        {
          label: `プロジェクト（費用）詳細`,
          path: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}`,
        },
      ]),
    []
  );

  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const { data: otherUsers } = useWorkspaceUsers({ identifier: _w_!.identifier, except_team_id: _t_!.id });
  const { mutate: createMutate, data, isPending, error: createMemberError } = useCreatePurchaseOrderMember();
  const {
    mutate: destroyMutate,
    data: destroyedData,
    isPending: isDestroyPending,
    error: destroyMemberError,
  } = useDestroyPurchaseOrderMember();
  const { invalidate: invalidateTeam } = useTeamInvalidate({ identifier: _w_!.identifier, id: _t_!.id });
  const { invalidate: invalidatePurchaseOrder } = usePurchaseOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const { invalidate: invalidateWorkspaceUsers } = useWorkspaceUsersInvalidate(_w_!.identifier);

  const defaultValues = {
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: _po_!.id,
    user_id: 0,
  };

  const {
    register: registerForTeamMember,
    handleSubmit: handleSubmitForTeamMember,
    formState: { errors: errorsForTeamMember },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues,
  });

  const {
    register: registerForGuest,
    handleSubmit: handleSubmitForGuest,
    formState: { errors: errorsForGuest },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues,
  });

  useEffect(() => setShowLoading(isPending || isDestroyPending), [isPending, isDestroyPending]);
  useEffect(() => toastApiError(createMemberError), [createMemberError]);
  useEffect(() => toastApiError(destroyMemberError), [destroyMemberError]);

  useEffect(() => {
    if (data || destroyedData) {
      invalidateTeam();
      invalidatePurchaseOrder();
      invalidateWorkspaceUsers();
    }
  }, [data, destroyedData]);

  const onSubmitForTeamMember: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    createMutate(data);
  };

  const onSubmitFroGuest: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    createMutate(data);
  };

  const onDestroyClick = (purchase_order_member_id: number) => {
    setShowLoading(true);
    destroyMutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      purchase_order_member_id,
    });
  };

  return (
    <WorkspaceLayout>
      <ScrollTop />

      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">関連メンバー</h5>
                </div>
              </div>
              <div className="card-body pt-0 row">
                <div className="form-group">
                  <label>プロジェクト名</label>
                  <p className="form-text text-muted text-sm ms-1">{_po_!.name}</p>
                </div>
                <div className="form-group">
                  <label>プロジェクト担当者</label>
                  <div className="d-flex align-items-center">
                    <img
                      src={_po_!.user.user_account.icon_file_url}
                      className="avatar avatar-sm me-2 rounded-circle"
                      alt="avatar image"
                    />
                    <h6 className="mb-0 text-sm">{_po_!.user.user_account.name}</h6>
                  </div>
                </div>
                <hr className="horizontal dark my-3" />
                <div className="col-12 col-md-6">
                  <form onSubmit={handleSubmitForTeamMember(onSubmitForTeamMember)}>
                    <div className="d-flex">
                      <div className="form-group me-2 flex-fill">
                        <label>メンバーの追加</label>
                        <select
                          className={`form-control ${errorsForTeamMember.user_id && 'is-invalid'}`}
                          required={true}
                          {...registerForTeamMember('user_id', {
                            validate: (d) => {
                              if (d == 0) return '選択して下さい';
                            },
                          })}
                        >
                          <option disabled={true} value="0">
                            選択してください
                          </option>
                          {_t_!.team_members
                            .filter((m) => _po_!.purchase_order_members.every((pom) => pom.user.id != m.user.id))
                            .map((m) => (
                              <option key={`po-tm-${m.id}`} value={m.user.id}>
                                {m.user.user_account.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="form-group flex-shrink-0">
                        <label>&nbsp;</label>
                        <button type="submit" className="btn w-100 btn-icon bg-gradient-dark">
                          <i className="fa fa-plus"></i>追加
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
                <div className="col-12 col-md-6">
                  <form onSubmit={handleSubmitForGuest(onSubmitFroGuest)}>
                    <div className="d-flex">
                      <div className="form-group me-2 flex-fill">
                        <label>チーム外ゲストの追加</label>
                        <select
                          className={`form-control ${errorsForGuest.user_id && 'is-invalid'}`}
                          required={true}
                          {...registerForGuest('user_id', {
                            validate: (d) => {
                              if (d == 0) return '選択して下さい';
                            },
                          })}
                        >
                          <option disabled={true} value={0}>
                            選択してください
                          </option>
                          {otherUsers &&
                            otherUsers
                              .filter((ou) => _po_!.purchase_order_members.every((pom) => pom.user.id != ou.id))
                              .filter((ou) => _t_!.team_members.every((tm) => tm.user.id != ou.id))
                              .map((u) => (
                                <option key={`po-other-${u.id}`} value={u.id}>
                                  {u.user_account.name}
                                </option>
                              ))}
                        </select>
                        <div className="text-xs text-muted mt-1">ゲストとしてこの案件のみアクセスできます</div>
                      </div>
                      <div className="form-group flex-shrink-0">
                        <label>&nbsp;</label>
                        <button className="btn w-100 btn-icon bg-gradient-dark">
                          <i className="fa fa-plus"></i>追加
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
                <hr className="horizontal dark my-3" />
                <div className="table-responsive">
                  <table className="table align-items-center">
                    <thead>
                      <tr>
                        <th
                          className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2"
                          style={{ width: 300 }}
                        >
                          メンバー
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2"></th>
                        <th className="text-secondary opacity-7" style={{ width: 55 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {_po_!.purchase_order_members.map((m) => (
                        <tr key={`po-m-${m.id}`}>
                          <td>
                            <div className="d-flex align-items-center">
                              <img
                                src={m.user.user_account.icon_file_url}
                                className="avatar avatar-sm me-2 rounded-circle"
                                alt="avatar image"
                              />
                              <h6 className="mb-0 text-sm">{m.user.user_account.name}</h6>
                            </div>
                          </td>
                          <td className="text-xs text-wrap">
                            {m.guest && <span className="badge badge-success badge-sm">Guest</span>}
                          </td>
                          <td>
                            {PurchaseOrderLogic.canUpdate(_t_!, _po_!, me!) && (
                              <button
                                type="button"
                                className="btn btn-icon-only bg-gradient-danger"
                                onClick={() => onDestroyClick(m.id)}
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                <Link to="" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                  プロジェクト詳細へ
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
